/**
 * Hero roster — single source of truth for playable characters.
 *
 * Consumed by CharSelectScene (display) and, later, combat + animation
 * systems (stats, move data). Full move data lands per-hero in
 * src/data/heroes/<id>.json via the character tickets (MY-1441..1445).
 */

import type { AnimState } from '@/systems/AnimationStateMachine';

export interface HeroStats {
  /** 1-10 scales matching the design doc. */
  speed: number;
  power: number;
  range: number;
  technique: number;
}

export interface HeroDef {
  id: string;
  name: string;
  /** Short epithet shown under the name on the select card. */
  epithet: string;
  heritage: string;
  /** One-line fighting-style summary. */
  style: string;
  stats: HeroStats;
  /** Placeholder accent colour until Soul-ID sprite art lands. */
  color: number;
  /** Locked heroes are hidden/greyed until unlocked in save state. */
  lockedByDefault: boolean;
  /** If true, LIGHT near any grounded enemy (not just stunned) triggers a grab. */
  commandThrow?: boolean;
  /** Sprite atlas assetId — renders the entity texture when loaded; falls back to colour body. */
  assetId?: string;
  /**
   * Per-state pose overrides (MY-1951). When a state key is present and the
   * assetId is loaded in the SpriteAtlas, that texture is used instead of the
   * base `assetId`. Composes with procedural locomotion transforms.
   * e.g. `{ attackL: 'ray_attack', attackH: 'ray_attack', hurt: 'ray_hurt', ko: 'ray_ko' }`
   */
  poseAssetIds?: Partial<Record<AnimState, string>>;
}

export const HEROES: readonly HeroDef[] = [
  {
    id: 'ray',
    name: 'RAY "TANK" MILLER',
    epithet: 'SHARP Bruiser',
    heritage: 'English · Camden',
    style: 'Boxing + grappling. Headbutt & pile-driver.',
    stats: { speed: 5, power: 9, range: 4, technique: 6 },
    color: 0xef233c,
    assetId: 'ray',
    lockedByDefault: false,
    commandThrow: true,
    // Hand-painted pose-swap sprites (MY-1951) — Higgsfield nano_banana, identity-locked to ray.png.
    poseAssetIds: {
      attackH: 'ray_attackh',
      hurt: 'ray_hurt',
      special: 'ray_special',
      ko: 'ray_ko',
      taunt: 'ray_taunt',
    },
  },
  {
    id: 'errol',
    name: 'ERROL "DREAD" CLARKE',
    epithet: 'Brixton Rhythm',
    heritage: 'Jamaican-British · Brixton',
    style: 'Cricket-bat swings, beat-timed combo bonus.',
    stats: { speed: 7, power: 8, range: 7, technique: 6 },
    color: 0x06d6a0,
    assetId: 'errol',
    lockedByDefault: false,
    // Hand-painted pose-swap sprites (MY-1951) — Higgsfield, identity-locked to errol.png.
    poseAssetIds: {
      attackH: 'errol_attackh',
      hurt: 'errol_hurt',
      special: 'errol_special',
      ko: 'errol_ko',
      taunt: 'errol_taunt',
    },
  },
  {
    id: 'kris',
    name: 'KRIS KOWALCZYK',
    epithet: 'GROM Tactical',
    heritage: 'Polish-British · Wembley',
    style: 'CQB + weapons. Hammer & crowbar.',
    stats: { speed: 7, power: 8, range: 6, technique: 8 },
    color: 0xffba08,
    assetId: 'kris',
    lockedByDefault: false,
    // Hand-painted pose-swap sprites (MY-1951) — Higgsfield, identity-locked to kris.png.
    poseAssetIds: {
      attackH: 'kris_attackh',
      hurt: 'kris_hurt',
      special: 'kris_special',
      ko: 'kris_ko',
      taunt: 'kris_taunt',
    },
  },
  {
    id: 'priya',
    name: 'PRIYA "FURY" KAUR',
    epithet: 'Muay Thai Medic',
    heritage: 'Indian-Punjabi-British · Southall',
    style: 'Aerial Muay Thai. Heals teammates.',
    stats: { speed: 9, power: 6, range: 6, technique: 9 },
    color: 0xff006e,
    assetId: 'priya',
    lockedByDefault: false,
    // Hand-painted pose-swap sprites (MY-1951) — Higgsfield, identity-locked to priya.png.
    poseAssetIds: {
      attackH: 'priya_attackh',
      hurt: 'priya_hurt',
      special: 'priya_special',
      ko: 'priya_ko',
      taunt: 'priya_taunt',
    },
  },
  {
    id: 'ghost',
    name: '"GHOST"',
    epithet: 'SAS Glass-Cannon',
    heritage: 'British · ex-22 SAS',
    style: 'Knife + flashbangs. High dmg, low HP.',
    stats: { speed: 8, power: 9, range: 5, technique: 10 },
    color: 0x8d99ae,
    assetId: 'ghost',
    lockedByDefault: true,
    // Hand-painted pose-swap sprites (MY-1951) — Higgsfield, identity-locked to ghost.png.
    poseAssetIds: {
      attackH: 'ghost_attackh',
      hurt: 'ghost_hurt',
      special: 'ghost_special',
      ko: 'ghost_ko',
      taunt: 'ghost_taunt',
    },
  },
  // ── 100% reward unlock — see MY-1991 / rewardTiers.ts ───────────────────────
  {
    id: 'praetor',
    name: 'THE PRAETOR',
    epithet: 'Last Line of the Establishment',
    heritage: 'British · Private Security',
    style: 'Riot-shield guard + shock baton. Tanky heavy; guard blocks frontal damage.',
    stats: { speed: 4, power: 10, range: 6, technique: 5 },
    color: 0x2d3a4a,
    assetId: 'the-praetor',
    poseAssetIds: {
      attackH: 'the-praetor_attackh',
      hurt: 'the-praetor_hurt',
      special: 'the-praetor_special',
      ko: 'the-praetor_ko',
    },
    lockedByDefault: true,
  },
  // ── 14/14 joke unlock — see MY-2002 / rewardTiers.ts ────────────────────────
  {
    id: 'halloway',
    name: 'THE RT HON. HALLOWAY',
    epithet: 'The Snivelling PM',
    heritage: 'British · Westminster',
    // Flail + briefcase slap; Bribe toss briefly stuns enemies; cower-dodge;
    // super "Call Security" summons a Praetorian who immediately walks off (gag).
    style: 'Flail, briefcase slap, Bribe toss (stuns), cower-dodge.',
    stats: { speed: 2, power: 1, range: 2, technique: 1 },
    color: 0xc8a45a,
    // Reuse PM weak-KO entity assets from MY-1978.
    assetId: 'pm-halloway',
    lockedByDefault: true,
  },
];

export function getHero(id: string): HeroDef | undefined {
  return HEROES.find((h) => h.id === id);
}
